import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { format } from 'date-fns';

const WorkOrderDetails = ({ workOrder, onClose, onComplete }) => {
  const [activeTab, setActiveTab] = useState('details');

  const getPriorityConfig = (priority) => {
    const configs = {
      low: { label: 'Low', color: 'text-muted-foreground', bg: 'bg-muted' },
      medium: { label: 'Medium', color: 'text-primary', bg: 'bg-primary/10' },
      high: { label: 'High', color: 'text-warning', bg: 'bg-warning/10' },
      urgent: { label: 'Urgent', color: 'text-error', bg: 'bg-error/10' }
    };
    return configs?.[priority] || configs?.medium;
  };

  const getStatusConfig = (status) => {
    const configs = {
      pending: { label: 'Pending', color: 'text-warning', bg: 'bg-warning/10' },
      in_progress: { label: 'In Progress', color: 'text-primary', bg: 'bg-primary/10' },
      completed: { label: 'Completed', color: 'text-success', bg: 'bg-success/10' },
      cancelled: { label: 'Cancelled', color: 'text-muted-foreground', bg: 'bg-muted' }
    };
    return configs?.[status] || configs?.pending;
  };

  const priorityConfig = getPriorityConfig(workOrder?.priority);
  const statusConfig = getStatusConfig(workOrder?.status);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end justify-center z-50" onClick={onClose}>
      <div
        className="bg-card rounded-t-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e?.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border p-4 flex items-center justify-between rounded-t-3xl">
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-bold text-foreground truncate">{workOrder?.title}</h2>
            <p className="text-sm text-muted-foreground">{workOrder?.work_order_number}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <Icon name="X" size={24} className="text-muted-foreground" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-2 px-4 pt-4 border-b border-border">
          {['details', 'checklist', 'photos', 'notes']?.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 text-sm font-medium capitalize transition-colors ${
                activeTab === tab
                  ? 'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="p-4">
          {activeTab === 'details' && (
            <div className="space-y-4">
              {/* Status and Priority */}
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium ${statusConfig?.bg} ${statusConfig?.color}`}>
                  {statusConfig?.label}
                </span>
                <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium ${priorityConfig?.bg} ${priorityConfig?.color}`}>
                  {priorityConfig?.label} Priority
                </span>
              </div>

              {/* Description */}
              <div>
                <h3 className="text-sm font-semibold text-foreground mb-2">Description</h3>
                <p className="text-sm text-muted-foreground">{workOrder?.description}</p>
              </div>

              {/* Equipment Info */}
              {workOrder?.equipment && (
                <div className="bg-muted/30 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-foreground mb-3">Equipment</h3>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Name</span>
                      <span className="text-sm font-medium text-foreground">{workOrder?.equipment?.name}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Location</span>
                      <span className="text-sm font-medium text-foreground">{workOrder?.equipment?.location}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Serial Number</span>
                      <span className="text-sm font-medium text-foreground">{workOrder?.equipment?.serial_number}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Schedule and Cost */}
              <div className="grid grid-cols-2 gap-4">
                {workOrder?.scheduled_date && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Scheduled Date</p>
                    <p className="text-sm font-medium text-foreground">
                      {format(new Date(workOrder?.scheduled_date), 'MMM dd, yyyy')}
                    </p>
                  </div>
                )}
                {workOrder?.estimated_cost && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Estimated Cost</p>
                    <p className="text-sm font-medium text-foreground">
                      ${workOrder?.estimated_cost?.toFixed(2)}
                    </p>
                  </div>
                )}
              </div>

              {/* Assigned To */}
              {workOrder?.assigned_to && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Assigned To</p>
                  <p className="text-sm font-medium text-foreground">{workOrder?.assigned_to?.full_name}</p>
                </div>
              )}

              {/* Notes */}
              {workOrder?.notes && (
                <div>
                  <h3 className="text-sm font-semibold text-foreground mb-2">Notes</h3>
                  <p className="text-sm text-muted-foreground">{workOrder?.notes}</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'checklist' && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">Task checklist coming soon...</p>
            </div>
          )}

          {activeTab === 'photos' && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">Photo documentation coming soon...</p>
            </div>
          )}

          {activeTab === 'notes' && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">Additional notes coming soon...</p>
            </div>
          )}
        </div>

        {/* Actions */}
        {workOrder?.status === 'in_progress' && (
          <div className="sticky bottom-0 bg-card border-t border-border p-4">
            <Button
              variant="primary"
              size="lg"
              iconName="CheckCircle2"
              iconPosition="left"
              onClick={onComplete}
              className="w-full"
            >
              Complete Work Order
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkOrderDetails;